import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import styled from "styled-components";
import validator from "validator";

import CloseIcon from "../../../../assets/icons/close.svg";
import Info from "../../../../components/form/Info";
import Label from "../../../../components/form/Label";
import PrimaryButton from "../../../../components/ui/PrimaryButton";
import TextButton from "../../../../components/ui/TextButton";

type ModalProps = {
  $isOpen: boolean;
};

type OverlayProps = {
  $isOpen: boolean;
};

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  justify-content: center;
  margin-bottom: 10px;
  width: 100%;
`;

const CloseButton = styled.img`
  cursor: pointer;
  height: 20px;
  position: absolute;
  right: 20px;
  top: 20px;
  width: 20px;

  &:hover {
    opacity: 0.8;
  }
`;

const ConfirmButton = styled(PrimaryButton)`
  align-self: center;
  background: var(--terbine-blue);
  font-size: clamp(14px, 2vw, 16px);
  padding: 10px;
  width: clamp(160px, 40%, 240px);

  &:disabled {
    background: lab(from var(--terbine-dark-gray) calc(l + 25) a b / 100);
    cursor: not-allowed;
  }

  &:hover:enabled {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const ErrorText = styled(Info)`
  font-size: clamp(12px, 2vw, 14px);
  text-align: left;
`;

const Form = styled.form`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  width: 100%;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 500px;
  width: 100%;
`;

const Modal = styled.div<ModalProps>`
  align-items: center;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  left: 50%;
  max-width: 500px;
  padding: clamp(20px, 5vw, 40px);
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  z-index: 110;
`;

const ModalDescription = styled.div`
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  font-size: clamp(14px, 2vw, 18px);
  max-width: 90%;
  text-align: center;
`;

const ModalTitle = styled.div`
  font-family: Oxygen, Inter, Roboto, sans-serif !important;
  font-size: clamp(18px, 4vw, 24px);
  font-weight: bold;
  text-align: center;
`;

const Overlay = styled.div<OverlayProps>`
  background-color: rgba(0, 0, 0, 0.38);
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 109;
`;

const ForgotPasswordModal = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [emailErr, setEmailErr] = useState<string | null>(null);

  const toggleModal = () => {
    setEmail("");
    setIsModalOpen(!isModalOpen);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (validator.isEmail("" + value)) {
      setEmailErr(null);
    } else {
      setEmailErr("Invalid email, please try again");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div>
      <TextButton
        aria-label="Forgot Password Button"
        onClick={toggleModal}
        type="button"
      >
        Forgot Password?
      </TextButton>

      <Overlay $isOpen={isModalOpen} onClick={toggleModal} />

      <Modal $isOpen={isModalOpen} id="modal">
        <CloseButton onClick={toggleModal} src={CloseIcon} />

        <Form onSubmit={handleSubmit}>
          <ModalTitle>Forgot Password?</ModalTitle>
          <ModalDescription>
            Enter your email address to reset it.
          </ModalDescription>

          <FormRow>
            <FormField>
              <Label $hasError={!Boolean(email) || Boolean(emailErr)}>Email</Label>
              <FormControl sx={{ m: 1, width: "100%" }}>
                <TextField
                  error={!Boolean(email) || Boolean(emailErr)}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="Enter your email"
                  size="small"
                  value={email}
                />
              </FormControl>
              {(!Boolean(email) || Boolean(emailErr)) && (
                <ErrorText $hasError={true}>
                  {!Boolean(email) ? "Email is required" : emailErr}
                </ErrorText>
              )}
            </FormField>
          </FormRow>

          <FormRow>
            <ButtonGroup>
              <ConfirmButton
                disabled={!Boolean(email) || Boolean(emailErr)}
                onClick={handleSubmit}
                type="submit"
              >
                Confirm
              </ConfirmButton>
            </ButtonGroup>
          </FormRow>
        </Form>
      </Modal>
    </div>
  );
};

export default ForgotPasswordModal;